import type { SearchBoxVM } from '@/react/view-models/SearchBoxVM';
import type { VoidCallback } from '@/type-utils';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import { msg, msgf } from '@/services/isomorphic/I18NService';
import {
  Breakpoints,
  Breakpoint
} from '@/react/components/core-ui/Breakpoints';
import { Link } from '@/react/components/core-ui/Link';
import { isNullOrEmpty } from '@/utils/null-utils';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { ProductSuggestionTile } from '../ProductSuggestionTile';

import S from './styles.module.scss';
import { search_box_frame_productsBySearch } from "@/lang/__generated__/ahnu/search_box_frame_productsBySearch";
import { search_box_frame_searchAll } from "@/lang/__generated__/ahnu/search_box_frame_searchAll";

export interface IProductsBySearchProps {
  /**
   * The `SearchBoxVM` instance to be used.
   */
  vm: SearchBoxVM;

  /**
   * The function to be called when the user navigates to a new page.
   */
  onNavigate: VoidCallback;
}

/**
 * Display a list of product suggestions based on the last search term inputted in SearchBoxVM.
 *
 * Displays as a `row` on larger screens and as a `grid` on smaller screens.
 */
export const ProductsBySearch: FC<IProductsBySearchProps> = observer(
  ({ vm, onNavigate }) => {
    const tiles = vm.productSuggestionsByTerm.map((suggestion, index) => (
      <ProductSuggestionTile
        // Composing the key using the suggestion SKU to ensure uniqueness
        key={`product-suggestion-${suggestion.sku}`}
        suggestion={suggestion}
        className={S.suggestedTile}
        onClick={() => {
          // Index is 0-based, so the position we send to the analytics needs an increment
          vm.dispatchProductClickAnalyticsEvent(suggestion, index + 1);
          onNavigate();
        }}
      />
    ));

    // Check to see if the 'Search All' link should be displayed inside the search dropdown menu.
    const showSearchAllLink =
      ConfigurationService.getConfig('search').getSetting(
        'showSearchAllLink'
      ).value;

    return (
      <>
        <div className={S.header}>
          <h3 className={S.title}>
            {msg(search_box_frame_productsBySearch)}
          </h3>
          {!isNullOrEmpty(vm.lastSearchTerm) && showSearchAllLink && (
            <Link
              // After a search is waited, we keep this prefetched to speed up navigation
              // in case the user clicks on it.
              className={S.searchLink}
              href={vm.encodeSearchResultsPageNavigationUrl()}
              onClick={onNavigate}
              variant="text"
            >
              <span>
                {msgf(search_box_frame_searchAll, {
                  term: vm.lastSearchTerm
                })}
              </span>
            </Link>
          )}
        </div>
        <Breakpoints>
          <Breakpoint default>
            <div className={S.productsRow}>{tiles}</div>
          </Breakpoint>
          <Breakpoint media="phone">
            <div className={S.productsGrid}>{tiles}</div>
          </Breakpoint>
        </Breakpoints>
      </>
    );
  }
);
